// Captura de evidencia de la entrega por etapa, guiada por pasos.
// Soporta VARIAS fotos a la vez (galería); se suben en serie con progreso.
import { useState } from 'react'
import { PhotoCapture } from '@/components/PhotoCapture'
import { Button } from '@/components/ui/Button'
import { uploadEvidence } from '@/services/evidence'
import { useGeolocation } from '@/hooks/useGeolocation'
import type { EvidenceStage } from '@/types'

const STAGE_INFO: Record<EvidenceStage, { n: string; label: string; desc: string }> = {
  carga: { n: '1', label: 'Carga', desc: 'La mercancía cargada en el camión.' },
  entrega: { n: '2', label: 'Entregada', desc: 'La mercancía ya entregada en la propiedad.' },
  acuse: { n: '3', label: 'Acuse físico', desc: 'Foto del acuse firmado en papel.' },
}
const STAGES = Object.keys(STAGE_INFO) as EvidenceStage[]

export default function EvidenceCapture({
  deliveryId,
  onUploaded,
  counts,
}: {
  deliveryId: string
  onUploaded?: () => void
  counts?: Record<EvidenceStage, number>
}) {
  const [stage, setStage] = useState<EvidenceStage>('carga')
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState('')
  const [ok, setOk] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const { getCurrent } = useGeolocation()

  const submit = async () => {
    if (files.length === 0) return
    setBusy(true)
    setOk(0)
    setError(null)
    try {
      const coords = await getCurrent().catch(() => null)
      let done = 0
      for (const f of files) {
        setProgress(`Subiendo ${done + 1} de ${files.length}…`)
        await uploadEvidence(deliveryId, f, {
          stage,
          taken_at: new Date().toISOString(),
          latitude: coords?.lat ?? null,
          longitude: coords?.lng ?? null,
        })
        done += 1
        onUploaded?.()
      }
      setOk(done)
      setFiles([])
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'No se pudieron subir todas las fotos.')
      onUploaded?.()
    } finally {
      setBusy(false)
      setProgress('')
    }
  }

  return (
    <div className="space-y-4">
      {/* Paso 1: elegir etapa */}
      <div>
        <p className="mb-2 text-sm font-semibold text-gray-800">1. ¿En qué momento son las fotos?</p>
        <div className="grid grid-cols-3 gap-2">
          {STAGES.map((s) => {
            const info = STAGE_INFO[s]
            const active = stage === s
            const count = counts?.[s] ?? 0
            return (
              <button
                key={s}
                onClick={() => setStage(s)}
                className={`relative rounded-xl border p-3 text-center transition ${
                  active
                    ? 'border-ds-500 bg-ds-50 ring-2 ring-ds-500/30'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className={`text-lg font-bold ${active ? 'text-ds-700' : 'text-gray-400'}`}>
                  {info.n}
                </div>
                <div className={`text-sm font-medium ${active ? 'text-ds-700' : 'text-gray-700'}`}>
                  {info.label}
                </div>
                {count > 0 && (
                  <span className="absolute right-1.5 top-1.5 rounded-full bg-green-100 px-1.5 text-[10px] font-bold text-green-700">
                    {count} 📷
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <p className="mt-1.5 text-xs text-gray-500">{STAGE_INFO[stage].desc}</p>
      </div>

      {/* Paso 2: tomar o elegir fotos */}
      <div>
        <p className="mb-2 text-sm font-semibold text-gray-800">
          2. Toma la foto o elige varias de tu galería
        </p>
        <PhotoCapture onCapture={setFiles} />
        {files.length > 1 && (
          <p className="mt-1 text-xs font-medium text-ds-700">
            {files.length} fotos seleccionadas
          </p>
        )}
      </div>

      {/* Paso 3: subir */}
      <div>
        <p className="mb-2 text-sm font-semibold text-gray-800">3. Guárdalas</p>
        <Button className="w-full" onClick={submit} disabled={files.length === 0 || busy}>
          {busy
            ? progress || 'Subiendo…'
            : files.length > 1
              ? `Subir ${files.length} fotos de "${STAGE_INFO[stage].label}"`
              : `Subir foto de "${STAGE_INFO[stage].label}"`}
        </Button>
        {files.length === 0 && !busy && ok === 0 && !error && (
          <p className="mt-1 text-center text-xs text-gray-400">Primero toma o elige fotos en el paso 2.</p>
        )}
        {ok > 0 && files.length === 0 && (
          <p className="mt-1 text-center text-xs font-medium text-green-600">
            ✓ {ok} foto{ok > 1 ? 's' : ''} subida{ok > 1 ? 's' : ''}. Puedes agregar más.
          </p>
        )}
        {error && <p className="mt-1 text-center text-xs text-red-600">{error}</p>}
      </div>
    </div>
  )
}
